<template>
  <div class="import-onboarding-container">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-file-import mr-3"></i>
          Import & Onboarding
        </h1>
        <p class="page-description">
          Upload a CSV/XLS/XLSX, map columns, preview changes, and apply to a project
        </p>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="modern-card info-card">
      <div class="card-header">
        <h3><i class="fas fa-info-circle mr-2"></i>How this import maps your data</h3>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <div class="info-icon">
              <i class="fas fa-bullseye"></i>
            </div>
            <div class="info-content">
              <h5>Scope</h5>
              <p>Choose <strong>Location-first</strong> to create a subject per location (recommended). Choose <strong>Entity-level</strong> to create one subject per entity.</p>
            </div>
          </div>
          
          <div class="info-item">
            <div class="info-icon">
              <i class="fas fa-link"></i>
            </div>
            <div class="info-content">
              <h5>Entity Matching</h5>
              <ul>
                <li>Primary: rows with the same <strong>Entity External ID</strong> attach to the same Entity.</li>
                <li>Fallback: if no external_id, we match by <strong>Name + City + State</strong> (may split multi-city entities).</li>
                <li>Tip: include <strong>EIN</strong> for better dedup later.</li>
              </ul>
            </div>
          </div>
          
          <div class="info-item">
            <div class="info-icon">
              <i class="fas fa-map-marker-alt"></i>
            </div>
            <div class="info-content">
              <h5>Location Creation</h5>
              <ul>
                <li>Primary: dedupe by <strong>Location: Center/Store Code</strong> per Entity.</li>
                <li>Fallback: creates by address (use City/State/Postal to reduce duplicates).</li>
                <li>Each CSV row represents <strong>one location</strong>.</li>
              </ul>
            </div>
          </div>
          
          <div class="info-item">
            <div class="info-icon">
              <i class="fas fa-cog"></i>
            </div>
            <div class="info-content">
              <h5>Process Flow</h5>
              <div class="process-steps">
                <span class="step">1. Select project</span>
                <i class="fas fa-arrow-right"></i>
                <span class="step">2. Prepare</span>
                <i class="fas fa-arrow-right"></i>
                <span class="step">3. Map columns</span>
                <i class="fas fa-arrow-right"></i>
                <span class="step">4. Validate (dry run)</span>
                <i class="fas fa-arrow-right"></i>
                <span class="step">5. Apply</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 1: Project Configuration -->
    <div class="modern-card step-card">
      <div class="card-header">
        <div class="step-indicator">
          <span class="step-number">1</span>
        </div>
        <div class="step-content">
          <h3>Choose Project & Configuration</h3>
          <p>Select the target project and configure import settings</p>
        </div>
      </div>
      <div class="card-body">
        <div class="form-section">
          <div class="form-group">
            <label class="form-label">Target Project</label>
            <select v-model="selectedProjectId" class="form-select">
              <option disabled value="">-- Select a project --</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name || ('Project #' + p.id) }}</option>
            </select>
            <div v-if="selectedProjectId" class="selected-info">
              <i class="fas fa-check-circle text-success mr-2"></i>
              Selected: {{ projectNameDisplay }}
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Subject Scope</label>
            <div class="scope-options">
              <div class="scope-option">
                <input type="radio" id="scope-location" value="location" v-model="scope">
                <label for="scope-location" class="scope-label">
                  <div class="scope-title">
                    <i class="fas fa-map-marker-alt mr-2"></i>
                    Location-first
                  </div>
                  <div class="scope-description">Create a subject per location (recommended)</div>
                </label>
              </div>
              <div class="scope-option">
                <input type="radio" id="scope-entity" value="entity" v-model="scope">
                <label for="scope-entity" class="scope-label">
                  <div class="scope-title">
                    <i class="fas fa-building mr-2"></i>
                    Entity-level
                  </div>
                  <div class="scope-description">Create one subject per entity</div>
                </label>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Default Values <small class="text-muted">(used when CSV cells are blank)</small></label>
            <div class="defaults-grid">
              <div class="default-group">
                <h6>Entity Defaults</h6>
                <div class="input-row">
                  <div class="input-group">
                    <label>Country</label>
                    <input v-model="defaults.country" class="form-control" placeholder="US" />
                  </div>
                  <div class="input-group">
                    <label>State</label>
                    <input v-model="defaults.state" class="form-control" placeholder="" />
                  </div>
                  <div class="input-group">
                    <label>Province</label>
                    <input v-model="defaults.province" class="form-control" placeholder="" />
                  </div>
                </div>
              </div>
              
              <div class="default-group">
                <h6>Location Defaults</h6>
                <div class="input-row">
                  <div class="input-group">
                    <label>Country</label>
                    <input v-model="defaults.location_country" class="form-control" placeholder="" />
                  </div>
                  <div class="input-group">
                    <label>State</label>
                    <input v-model="defaults.location_state" class="form-control" placeholder="" />
                  </div>
                  <div class="input-group">
                    <label>Province</label>
                    <input v-model="defaults.location_province" class="form-control" placeholder="" />
                  </div>
                </div>
                
                <div class="checkbox-group">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="defaults.active" />
                    <span class="checkmark"></span>
                    Active by default
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: File Upload -->
    <div class="modern-card step-card">
      <div class="card-header">
        <div class="step-indicator">
          <span class="step-number">2</span>
        </div>
        <div class="step-content">
          <h3>Upload File</h3>
          <p>Select your CSV, XLS, or XLSX file to import</p>
        </div>
      </div>
      <div class="card-body">
        <div class="upload-section">
          <div class="file-upload-area" :class="{ 'has-file': fileName }">
            <input type="file" @change="onFileChange" accept=".csv,.xls,.xlsx" id="file-input" class="file-input" />
            <label for="file-input" class="file-upload-label">
              <div class="upload-icon">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <div class="upload-text">
                <span v-if="!fileName" class="upload-prompt">Click to select file or drag and drop</span>
                <span v-else class="file-selected">
                  <i class="fas fa-file-excel mr-2"></i>{{ fileName }}
                </span>
              </div>
              <div class="upload-hint">Supports CSV, XLS, XLSX files</div>
            </label>
          </div>
          
          <div class="upload-options">
            <label class="checkbox-label">
              <input type="checkbox" v-model="hasHeader" />
              <span class="checkmark"></span>
              First row has headers
            </label>
          </div>
          
          <div class="upload-actions">
            <button 
              :disabled="!file" 
              @click="prepare"
              class="btn btn-primary btn-lg"
              :class="{ 'btn-loading': preparing }"
            >
              <i class="fas fa-cog mr-2" v-if="!preparing"></i>
              <i class="fas fa-spinner fa-spin mr-2" v-else></i>
              {{ preparing ? 'Preparing...' : 'Prepare File' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Column Mapping -->
    <div v-if="columns.length" class="modern-card step-card">
      <div class="card-header">
        <div class="step-indicator">
          <span class="step-number">3</span>
        </div>
        <div class="step-content">
          <h3>Map Columns</h3>
          <p>Match your CSV columns to the target fields</p>
        </div>
      </div>
      <div class="card-body">
        <div class="mapping-section">
          <div class="mapping-help">
            <i class="fas fa-info-circle mr-2"></i>
            Select the source CSV column for each target field below. Fields marked as "Ignore" will not be imported.
          </div>
          
          <div class="mapping-groups">
            <!-- Required Fields -->
            <div class="field-group required-fields">
              <div class="group-header">
                <h5><i class="fas fa-star text-warning mr-2"></i>Required Fields</h5>
                <p>Essential fields for entity identification and matching</p>
              </div>
              <div class="fields-list">
                <div v-for="field in getRequiredFields()" :key="field.value" class="field-mapping-row">
                  <div class="field-info">
                    <div class="field-name">{{ field.label }}</div>
                    <div class="field-description">{{ getFieldDescription(field.value) }}</div>
                  </div>
                  <div class="field-mapping">
                    <select v-model="mapping[field.value]" class="form-select" :class="{ 'required': isFieldRequired(field.value) }">
                      <option value="">{{ isFieldRequired(field.value) ? 'Select column...' : 'Ignore' }}</option>
                      <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Entity Information -->
            <div class="field-group entity-fields">
              <div class="group-header">
                <h5><i class="fas fa-building text-primary mr-2"></i>Entity Information</h5>
                <p>Company or organization details</p>
              </div>
              <div class="fields-list">
                <div v-for="field in getEntityFields()" :key="field.value" class="field-mapping-row">
                  <div class="field-info">
                    <div class="field-name">{{ field.label }}</div>
                    <div class="field-description">{{ getFieldDescription(field.value) }}</div>
                  </div>
                  <div class="field-mapping">
                    <select v-model="mapping[field.value]" class="form-select">
                      <option value="">Ignore</option>
                      <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Location Information -->
            <div class="field-group location-fields" v-if="scope === 'location'">
              <div class="group-header">
                <h5><i class="fas fa-map-marker-alt text-success mr-2"></i>Location Information</h5>
                <p>Specific location or branch details (for location-first scope)</p>
              </div>
              <div class="fields-list">
                <div v-for="field in getLocationFields()" :key="field.value" class="field-mapping-row">
                  <div class="field-info">
                    <div class="field-name">{{ field.label }}</div>
                    <div class="field-description">{{ getFieldDescription(field.value) }}</div>
                  </div>
                  <div class="field-mapping">
                    <select v-model="mapping[field.value]" class="form-select">
                      <option value="">Ignore</option>
                      <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contact Information -->
            <div class="field-group contact-fields">
              <div class="group-header">
                <h5><i class="fas fa-id-card text-success mr-2"></i>Contact Information</h5>
                <p>Individual contact details (agents, representatives, etc.)</p>
              </div>
              <div class="fields-list">
                <div v-for="field in getContactFields()" :key="field.value" class="field-mapping-row">
                  <div class="field-info">
                    <div class="field-name">{{ field.label }}</div>
                    <div class="field-description">{{ getFieldDescription(field.value) }}</div>
                  </div>
                  <div class="field-mapping">
                    <select v-model="mapping[field.value]" class="form-select">
                      <option value="">Ignore</option>
                      <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Subject Settings -->
            <div class="field-group subject-fields">
              <div class="group-header">
                <h5><i class="fas fa-user-check text-info mr-2"></i>Subject Settings</h5>
                <p>Compliance subject configuration and contact information</p>
              </div>
              <div class="fields-list">
                <div v-for="field in getSubjectFields()" :key="field.value" class="field-mapping-row">
                  <div class="field-info">
                    <div class="field-name">{{ field.label }}</div>
                    <div class="field-description">{{ getFieldDescription(field.value) }}</div>
                  </div>
                  <div class="field-mapping">
                    <select v-model="mapping[field.value]" class="form-select">
                      <option value="">Ignore</option>
                      <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="mapping-actions">
            <button 
              :disabled="!columns.length" 
              @click="validate"
              class="btn btn-primary btn-lg"
              :class="{ 'btn-loading': validating }"
            >
              <i class="fas fa-check mr-2" v-if="!validating"></i>
              <i class="fas fa-spinner fa-spin mr-2" v-else></i>
              {{ validating ? 'Validating...' : 'Validate Mapping' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 4: Preview & Apply -->
    <div v-if="preview.rows && preview.rows.length" class="modern-card step-card">
      <div class="card-header">
        <div class="step-indicator">
          <span class="step-number">4</span>
        </div>
        <div class="step-content">
          <h3>Preview & Apply Changes</h3>
          <p>Review the proposed changes before applying to your project</p>
        </div>
      </div>
      <div class="card-body">
        <div class="preview-section">
          <!-- Summary Cards -->
          <div class="preview-summary">
            <div class="summary-card adds">
              <div class="summary-icon">
                <i class="fas fa-plus"></i>
              </div>
              <div class="summary-content">
                <div class="summary-value">{{ preview.summary.adds }}</div>
                <div class="summary-label">New Records</div>
              </div>
            </div>
            
            <div class="summary-card updates">
              <div class="summary-icon">
                <i class="fas fa-edit"></i>
              </div>
              <div class="summary-content">
                <div class="summary-value">{{ preview.summary.updates }}</div>
                <div class="summary-label">Updates</div>
              </div>
            </div>
            
            <div class="summary-card deactivations">
              <div class="summary-icon">
                <i class="fas fa-minus-circle"></i>
              </div>
              <div class="summary-content">
                <div class="summary-value">{{ preview.summary.deactivations }}</div>
                <div class="summary-label">Deactivations</div>
              </div>
            </div>
          </div>
          
          <!-- Preview Table -->
          <div class="preview-table-section">
            <div class="table-header">
              <h5>Preview Changes (showing first 50 rows)</h5>
              <div class="table-info">
                Total: {{ preview.rows.length }} rows
              </div>
            </div>
            
            <div class="table-wrapper">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>External ID</th>
                    <th>Name</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Active</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in preview.rows.slice(0, 50)" :key="idx" :class="getRowClass(r.action)">
                    <td>
                      <span class="action-badge" :class="r.action.toLowerCase()">
                        <i :class="getActionIcon(r.action)" class="mr-1"></i>
                        {{ r.action }}
                      </span>
                    </td>
                    <td>{{ r.external_id || '' }}</td>
                    <td>{{ r.name || '' }}</td>
                    <td>{{ r.city || '' }}</td>
                    <td>{{ r.state || '' }}</td>
                    <td>
                      <span class="status-badge" :class="r.active === true ? 'active' : 'inactive'">
                        {{ r.active === true ? 'TRUE' : (r.active === false ? 'FALSE' : '') }}
                      </span>
                    </td>
                    <td>
                      <div v-if="r.issues && r.issues.length" class="issues-list">
                        <span v-for="issue in r.issues" :key="issue" class="issue-tag">
                          {{ issue }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div class="apply-actions">
            <button 
              :disabled="!preview.rows.length" 
              @click="apply"
              class="btn btn-success btn-lg"
              :class="{ 'btn-loading': applying }"
            >
              <i class="fas fa-check mr-2" v-if="!applying"></i>
              <i class="fas fa-spinner fa-spin mr-2" v-else></i>
              {{ applying ? 'Applying Changes...' : 'Apply Changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ImportOnboarding',
  data() {
    return {
      file: null,
      fileName: '',
      hasHeader: true,
      columns: [],
      mapping: {},
      projects: [],
      selectedProjectId: '',
      scope: 'location',
      defaults: { country: 'US', state: '', province: '', location_country: '', location_state: '', location_province: '', active: true },
      targetFields: [
        { value: 'external_id',   label: 'External ID (Client/System Matching Key)' },
        { value: 'name',          label: 'Entity: Name' },
        { value: 'ein',           label: 'Entity: EIN / Tax ID' },
        { value: 'email',         label: 'Entity: Primary Email' },
        { value: 'phone',         label: 'Entity: Primary Phone' },
        { value: 'address_line1', label: 'Entity: Address Line 1' },
        { value: 'address_line2', label: 'Entity: Address Line 2' },
        { value: 'city',          label: 'Entity: City' },
        { value: 'state',         label: 'Entity: State' },
        { value: 'province',      label: 'Entity: Province / Region' },
        { value: 'postal_code',   label: 'Entity: Postal Code' },
        { value: 'country',       label: 'Entity: Country' },
        { value: 'active',        label: 'Subject: Active (TRUE/FALSE)' },
        { value: 'agent_email',   label: 'Subject: Agent/Broker Email' },
        // Location scope (optional)
        { value: 'location_code', label: 'Location: Center/Store Code' },
        { value: 'location_name', label: 'Location: Display Name' },
        { value: 'location_address_line1', label: 'Location: Address Line 1' },
        { value: 'location_address_line2', label: 'Location: Address Line 2' },
        { value: 'location_city', label: 'Location: City' },
        { value: 'location_state', label: 'Location: State' },
        { value: 'location_province', label: 'Location: Province / Region' },
        { value: 'location_postal_code', label: 'Location: Postal Code' },
        { value: 'location_country', label: 'Location: Country' },
        // Contact fields (for importing agent contacts)
        { value: 'contact_name', label: 'Contact: Name' },
        { value: 'contact_email', label: 'Contact: Email' },
        { value: 'contact_phone', label: 'Contact: Phone' },
        { value: 'contact_title', label: 'Contact: Title/Role' },
        { value: 'contact_is_primary', label: 'Contact: Is Primary (TRUE/FALSE)' },
      ],
      preview: { rows: [], summary: { adds: 0, updates: 0, deactivations: 0 }},
      preparing: false,
      validating: false,
      applying: false,
    }
  },
  computed: {
    projectId() {
      return this.selectedProjectId
    },
    projectNameDisplay() {
      const p = this.projects.find(x => String(x.id) === String(this.selectedProjectId))
      return p ? (p.name || ('Project #' + p.id)) : ''
    }
  },
  async mounted() {
    // Seed initial selection from route if present
    const fromQuery = this.$route.query.project_id || ''
    if (fromQuery) this.selectedProjectId = String(fromQuery)
    try {
      const res = await api.get('/projects')
      // Backend returns { projects: [...], count, success }
      if (res.data && Array.isArray(res.data.projects)) {
        this.projects = res.data.projects
      } else if (Array.isArray(res.data)) {
        this.projects = res.data
      } else if (Array.isArray(res.data?.results)) {
        this.projects = res.data.results
      }
      // If only one project, auto-select
      if (!this.selectedProjectId && this.projects.length === 1) {
        this.selectedProjectId = String(this.projects[0].id)
      }
    } catch (e) {
      // Non-fatal; user can still proceed if API restricted
    }
  },
  methods: {
    onFileChange(e) {
      const f = e.target.files[0]
      this.file = f || null
      this.fileName = f ? f.name : ''
    },
    getFieldCategory(fieldValue) {
      if (fieldValue.startsWith('location_')) return 'Location'
      if (fieldValue.startsWith('contact_')) return 'Contact'
      if (['external_id', 'name', 'ein'].includes(fieldValue)) return 'Entity'
      if (['active', 'agent_email'].includes(fieldValue)) return 'Subject'
      return 'Entity'
    },
    getActionIcon(action) {
      const icons = {
        'ADD': 'fas fa-plus',
        'UPDATE': 'fas fa-edit', 
        'DEACTIVATE': 'fas fa-minus-circle'
      }
      return icons[action] || 'fas fa-circle'
    },
    getRowClass(action) {
      return `row-${action.toLowerCase()}`
    },
    getRequiredFields() {
      return this.targetFields.filter(field => ['external_id', 'name'].includes(field.value))
    },
    getEntityFields() {
      return this.targetFields.filter(field => 
        field.value.startsWith('address_') || 
        ['ein', 'email', 'phone', 'city', 'state', 'province', 'postal_code', 'country'].includes(field.value)
      )
    },
    getLocationFields() {
      return this.targetFields.filter(field => field.value.startsWith('location_'))
    },
    getContactFields() {
      return this.targetFields.filter(field => field.value.startsWith('contact_'))
    },
    getSubjectFields() {
      return this.targetFields.filter(field => ['active', 'agent_email'].includes(field.value))
    },
    isFieldRequired(fieldValue) {
      return ['external_id', 'name'].includes(fieldValue)
    },
    getFieldDescription(fieldValue) {
      const descriptions = {
        'external_id': 'Unique identifier from your system for matching existing records',
        'name': 'Company or entity name (required for all imports)',
        'ein': 'Federal Tax ID number for entity verification',
        'email': 'Primary contact email for the entity',
        'phone': 'Main phone number for the entity',
        'address_line1': 'Street address of the entity headquarters',
        'address_line2': 'Additional address information (suite, unit, etc.)',
        'city': 'City where the entity is located',
        'state': 'State or region for the entity',
        'province': 'Province (for non-US entities)',
        'postal_code': 'ZIP code or postal code',
        'country': 'Country code (e.g., US, CA, UK)',
        'active': 'Whether this subject should be active (TRUE/FALSE)',
        'agent_email': 'Insurance agent or broker email contact',
        'location_code': 'Unique identifier for this specific location/branch',
        'location_name': 'Display name for this location (e.g., "Downtown Store")',
        'location_address_line1': 'Street address of this specific location',
        'location_address_line2': 'Additional location address information',
        'location_city': 'City where this location is situated',
        'location_state': 'State or region for this location',
        'location_province': 'Province for this location (non-US)',
        'location_postal_code': 'ZIP code for this specific location',
        'location_country': 'Country code for this location',
        'contact_name': 'Full name of the contact person (e.g., insurance agent)',
        'contact_email': 'Email address for this contact',
        'contact_phone': 'Phone number for this contact',
        'contact_title': 'Job title or role (e.g., "Insurance Agent", "Account Manager")',
        'contact_is_primary': 'Whether this is the primary contact for the entity (TRUE/FALSE)'
      }
      return descriptions[fieldValue] || 'Additional field for enhanced data matching'
    },
    async prepare() {
      this.preparing = true
      try {
        const form = new FormData()
        form.append('file', this.file)
        form.append('has_header', this.hasHeader ? 'true' : 'false')
        if (this.projectId) form.append('project_id', this.projectId)
        const res = await api.post('/imports/prepare/', form, { headers: { 'Content-Type': 'multipart/form-data' } })
        this.columns = res.data.columns || []
        this.mapping = res.data.suggested_mapping || {}
        this.prepareToken = (res.data.token || '').trim()
      } finally {
        this.preparing = false
      }
    },
    async validate() {
      if (!this.selectedProjectId) {
        alert('Please select a project first.')
        return
      }
      
      this.validating = true
      try {
        const payload = {
          token: this.prepareToken,
          mapping: this.mapping,
          project_id: this.selectedProjectId,
          has_header: this.hasHeader,
          scope: this.scope,
          defaults: this.defaults,
        }
        
        if (!payload.token) {
          alert('Please click Prepare first to generate a session token.')
          return
        }
        
        const res = await api.post('/imports/validate/', payload)
        this.preview = res.data || { rows: [], summary: { adds: 0, updates: 0, deactivations: 0 }}
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Validation failed'
        alert(`Validate error: ${msg}`)
        throw e
      } finally {
        this.validating = false
      }
    },
    async apply() {
      if (!this.selectedProjectId) {
        alert('Please select a project first.')
        return
      }
      
      this.applying = true
      try {
        const payload = {
          project_id: this.selectedProjectId,
          mapping: this.mapping,
          token: this.prepareToken,
          scope: this.scope,
          defaults: this.defaults
        }
        await api.post('/imports/apply/', payload)
        alert('Apply initiated. Check logs for details.')
      } finally {
        this.applying = false
      }
    }
  }
}
</script>

<style scoped>
/* Main Container */
.import-onboarding-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Header Section */
.header-section {
  margin-bottom: 2rem;
}

.header-content {
  text-align: center;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.page-description {
  font-size: 1.2rem;
  color: #6c757d;
  margin-bottom: 0;
}

/* Modern Cards */
.modern-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.info-card .card-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.step-indicator {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.step-number {
  font-size: 1.5rem;
  font-weight: bold;
}

.step-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
}

.step-content p {
  margin: 0;
  opacity: 0.9;
}

.card-body {
  padding: 2rem;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.info-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.info-content h5 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.info-content ul {
  margin: 0.5rem 0 0 0;
  padding-left: 1.2rem;
}

.info-content li {
  margin-bottom: 0.25rem;
  color: #495057;
}

.process-steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.step {
  background: #e9ecef;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.process-steps i {
  color: #6c757d;
  font-size: 0.8rem;
}

/* Form Elements */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.form-select {
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  outline: none;
}

.selected-info {
  color: #28a745;
  font-weight: 500;
  margin-top: 0.5rem;
}

/* Scope Options */
.scope-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.scope-option {
  position: relative;
}

.scope-option input[type="radio"] {
  position: absolute;
  opacity: 0;
}

.scope-label {
  display: block;
  padding: 1.5rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.scope-option input[type="radio"]:checked + .scope-label {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
}

.scope-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.scope-description {
  color: #6c757d;
  font-size: 0.9rem;
}

/* Defaults Grid */
.defaults-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.default-group h6 {
  color: #495057;
  margin-bottom: 1rem;
  font-weight: 600;
}

.input-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.input-group {
  display: flex;
  flex-direction: column;
}

.input-group label {
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.form-control {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  outline: none;
}

/* Checkbox Styling */
.checkbox-group {
  margin-top: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #495057;
}

.checkbox-label input[type="checkbox"] {
  position: absolute;
  opacity: 0;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #ced4da;
  border-radius: 4px;
  margin-right: 0.75rem;
  position: relative;
  transition: all 0.3s ease;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark {
  background: #667eea;
  border-color: #667eea;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* File Upload */
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.file-upload-area {
  border: 2px dashed #ced4da;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.file-upload-area.has-file {
  border-color: #28a745;
  background: rgba(40, 167, 69, 0.05);
}

.file-input {
  display: none;
}

.file-upload-label {
  cursor: pointer;
  display: block;
}

.upload-icon {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.has-file .upload-icon {
  color: #28a745;
}

.upload-text {
  font-size: 1.1rem;
  color: #495057;
  margin-bottom: 0.5rem;
}

.file-selected {
  color: #28a745;
  font-weight: 600;
}

.upload-hint {
  color: #6c757d;
  font-size: 0.9rem;
}

.upload-options {
  display: flex;
  justify-content: center;
}

.upload-actions {
  display: flex;
  justify-content: center;
}

/* Mapping Section */
.mapping-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.mapping-help {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 8px;
  padding: 1rem;
  color: #1565c0;
}

/* Field Groups */
.mapping-groups {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.field-group {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.group-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.group-header h5 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.group-header p {
  margin: 0;
  color: #6c757d;
  font-size: 0.95rem;
}

.fields-list {
  padding: 1rem;
}

.field-mapping-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 1rem;
  border: 1px solid #f1f3f4;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
}

.field-mapping-row:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.field-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1rem;
}

.field-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.field-mapping {
  display: flex;
  align-items: center;
}

.form-select.required {
  border-color: #ffc107;
  background: rgba(255, 193, 7, 0.05);
}

.form-select.required:focus {
  border-color: #ffc107;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
}

/* Field Group Specific Styling */
.required-fields {
  border-color: #ffc107;
}

.required-fields .group-header {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-bottom-color: #ffc107;
}

.entity-fields {
  border-color: #007bff;
}

.entity-fields .group-header {
  background: linear-gradient(135deg, #cce5ff 0%, #b3d9ff 100%);
  border-bottom-color: #007bff;
}

.location-fields {
  border-color: #28a745;
}

.location-fields .group-header {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-bottom-color: #28a745;
}

.subject-fields {
  border-color: #17a2b8;
}

.subject-fields .group-header {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  border-bottom-color: #17a2b8;
}

.mapping-actions {
  display: flex;
  justify-content: center;
}

/* Preview Section */
.preview-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.preview-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.summary-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 8px;
  gap: 1rem;
}

.summary-card.adds {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border: 1px solid #c3e6cb;
}

.summary-card.updates {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 1px solid #ffeaa7;
}

.summary-card.deactivations {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  border: 1px solid #f5c6cb;
}

.summary-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.adds .summary-icon {
  background: #28a745;
  color: white;
}

.updates .summary-icon {
  background: #ffc107;
  color: white;
}

.deactivations .summary-icon {
  background: #dc3545;
  color: white;
}

.summary-content {
  display: flex;
  flex-direction: column;
}

.summary-value {
  font-size: 2rem;
  font-weight: bold;
  color: #2c3e50;
}

.summary-label {
  color: #6c757d;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Preview Table */
.preview-table-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.table-header h5 {
  margin: 0;
  color: #2c3e50;
}

.table-info {
  color: #6c757d;
  font-size: 0.9rem;
}

.table-wrapper {
  overflow-x: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
}

.preview-table th {
  background: #2c3e50;
  color: white;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: none;
}

.preview-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.preview-table tr:hover {
  background: #f8f9fa;
}

/* Row Classes */
.row-add {
  background: rgba(40, 167, 69, 0.05);
}

.row-update {
  background: rgba(255, 193, 7, 0.05);
}

.row-deactivate {
  background: rgba(220, 53, 69, 0.05);
}

/* Action Badges */
.action-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.action-badge.add {
  background: #28a745;
  color: white;
}

.action-badge.update {
  background: #ffc107;
  color: #212529;
}

.action-badge.deactivate {
  background: #dc3545;
  color: white;
}

/* Status Badges */
.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.active {
  background: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background: #f8d7da;
  color: #721c24;
}

/* Issues */
.issues-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.issue-tag {
  background: #fff3cd;
  color: #856404;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
  color: white;
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(86, 171, 47, 0.4);
}

.btn-lg {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

.btn-loading {
  position: relative;
}

/* Action Buttons */
.upload-actions,
.mapping-actions,
.apply-actions {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .import-onboarding-container {
    padding: 1rem;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .card-header {
    padding: 1rem;
  }
  
  .card-body {
    padding: 1rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .scope-options {
    grid-template-columns: 1fr;
  }
  
  .defaults-grid {
    grid-template-columns: 1fr;
  }
  
  .input-row {
    grid-template-columns: 1fr;
  }
  
  .mapping-header,
  .mapping-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .preview-summary {
    grid-template-columns: 1fr;
  }
  
  .process-steps {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
